import React, { useEffect, useState } from 'react';
import './SessionAnalytics.css';
import { Box, Typography, Grid } from '@mui/material';
import AnalyticsCard, {
  readGrowth,
  SectionUnavailable,
  settledData,
} from '../AnalyticsCard/AnalyticsCard';
import SessionLineChart from './SessionLineChart/SessionLineChart';
import DonutChart from '../DonutChart/DonutChart';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { CardSkeleton, DonutSkeleton } from '../../SkeletonLoader/SkeletonLoader';
import '../../SkeletonLoader/SkeletonLoader.css';
import GroupSessionIcon from '../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import OneOnOneSessionIcon from '../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import PrimeSessionIcon from '../../../../assets/Admin/SessionIcons/prime-session-icon.svg';

const SessionAnalytics = ({ timePeriod = 'last_month' }) => {
  const [sessionMetricsData, setSessionMetricsData] = useState(null);
  const [cancellationMetricsData, setCancellationMetricsData] = useState(null);
  const [repeatSessionData, setRepeatSessionData] = useState(null);
  const [subjectDemandData, setSubjectDemandData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        // Fetch all APIs in parallel
        const [
          sessionMetricsResponse,
          cancellationMetricsResponse,
          repeatSessionResponse,
          subjectDemandResponse,
        ] = await Promise.allSettled([
          axiosInstance.get(
            `/admin/dashboard/session-metrics?period=${mapTimePeriodForNewAPIs(timePeriod)}`
          ),
          axiosInstance.get(
            `/admin/dashboard/session-cancellation-metrics?period=${mapTimePeriodForNewAPIs(timePeriod)}`
          ),
          axiosInstance.get(
            `/admin/dashboard/repeat-session-metrics?period=${mapTimePeriodForNewAPIs(timePeriod)}`
          ),
          axiosInstance.get(
            `/admin/dashboard/subject-demand-analytics?period=${mapTimePeriodForSubjectDemand(timePeriod)}`
          ),
        ]);

        // allSettled: a failed request only blanks its own section (shown as
        // "--"); the others still show their data.
        if (isMounted) {
          setSessionMetricsData(settledData(sessionMetricsResponse, 'session metrics'));
          setCancellationMetricsData(
            settledData(cancellationMetricsResponse, 'session cancellation metrics')
          );
          setRepeatSessionData(settledData(repeatSessionResponse, 'repeat session metrics'));
          setSubjectDemandData(settledData(subjectDemandResponse, 'subject demand analytics'));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAnalytics();
    return () => {
      isMounted = false;
    };
  }, [timePeriod]);

  // Prepare data for cards from new APIs
  const totalSessions = sessionMetricsData?.total_sessions?.value ?? 0;
  const totalSessionsGrowth = readGrowth(sessionMetricsData?.total_sessions?.growth_percentage);

  const bookingRatio = sessionMetricsData?.booking_ratio?.value ?? 0;
  const bookingRatioGrowth = readGrowth(sessionMetricsData?.booking_ratio?.growth_percentage);

  const avgBookingTime = sessionMetricsData?.avg_booking_time?.value ?? 0;
  const avgBookingTimeGrowth = readGrowth(sessionMetricsData?.avg_booking_time?.growth_percentage);

  const completionRate = sessionMetricsData?.completion_rate?.value ?? 0;
  const completionRateGrowth = readGrowth(sessionMetricsData?.completion_rate?.growth_percentage);

  const formatAvgBookingTime = hours => {
    const h = Number(hours) || 0;
    if (h >= 24) {
      const days = Math.floor(h / 24);
      const remainingHrs = h % 24;
      const dayLabel = days === 1 ? 'day' : 'days';
      if (remainingHrs === 0) return `${days} ${dayLabel}`;
      const hrsLabel = remainingHrs === 1 ? 'hr' : 'hrs';
      return `${days} ${dayLabel} ${Number(remainingHrs.toFixed(1))} ${hrsLabel}`;
    }
    const hrsLabel = h === 1 ? 'hr' : 'hrs';
    return `${Number(h.toFixed(1))} ${hrsLabel}`;
  };
  const avgBookingTimeDisplay = formatAvgBookingTime(avgBookingTime);

  // Repeat session rate from new API
  const repeatSessionRate = repeatSessionData?.repeat_session_rate?.value ?? 0;
  const repeatSessionRateGrowth = readGrowth(
    repeatSessionData?.repeat_session_rate?.growth_percentage
  );

  // Session Type donut: Total Sessions split by type, for the selected period
  // (same API and rules as the Total Sessions card, so the totals match)
  const sessionsByType = sessionMetricsData?.sessions_by_type;
  const sessionTypeData = [
    { label: 'One-On-One', value: sessionsByType?.one_on_one ?? 0 },
    { label: 'Group', value: sessionsByType?.group ?? 0 },
    { label: 'Prime', value: sessionsByType?.prime ?? 0 },
  ];
  const donutTotal = sessionTypeData.reduce((sum, item) => sum + item.value, 0);

  // Cancellation data from new API
  const sessionCancellation = {
    overall: cancellationMetricsData?.cancellation_rate?.overall ?? 0,
    byStudent: cancellationMetricsData?.cancellation_rate?.by_learner ?? 0,
    byTeacher: cancellationMetricsData?.cancellation_rate?.by_instructor ?? 0,
    byType: [
      {
        label: 'One-On-One',
        value: cancellationMetricsData?.cancelled_sessions?.one_on_one ?? 0,
      },
      {
        label: 'Group',
        value: cancellationMetricsData?.cancelled_sessions?.group ?? 0,
      },
      {
        label: 'Prime',
        value: cancellationMetricsData?.cancelled_sessions?.prime ?? 0,
      },
    ],
  };

  // Process cancellation reasons from new API
  const processCancellationReasons = reasons => {
    if (!reasons || reasons.length === 0) {
      return [{ label: 'No cancellation data available', value: 0 }];
    }

    return reasons.map((reason, index) => ({
      label: reason.reason || `Reason ${index + 1}`,
      value: reason.count || 0,
    }));
  };

  const cancellationReasons = processCancellationReasons(
    cancellationMetricsData?.major_cancellation_reasons
  );

  // Only show top 5 cancellation reasons by value (descending)
  const topCancellationReasons =
    cancellationReasons.length > 1 &&
    cancellationReasons[0].label !== 'No cancellation data available'
      ? [...cancellationReasons].sort((a, b) => b.value - a.value).slice(0, 5)
      : cancellationReasons;

  // Get appropriate comparison period text for the selected time period
  const getComparisonPeriodText = () => {
    switch (timePeriod) {
      case 'last_day':
        return 'previous day';
      case 'last_week':
        return 'previous week';
      case 'last_2_weeks':
        return 'last 2 weeks';
      case 'last_month':
        return 'last month';
      case 'last_3_months':
        return 'last 3 months';
      case 'last_6_months':
        return 'last 6 months';
      case 'last_year':
        return 'previous year';
      default:
        return 'last month';
    }
  };

  // Custom card for listing cancellation reasons (merged)
  const CancellationReasonsCard = ({ data }) => (
    <div className="duration-list-card">
      <div className="duration-list-title">Major Cancellation Reasons</div>
      <div className="duration-list-content">
        {data.map(item => (
          <div className="duration-list-item" key={item.label}>
            <span className="duration-list-label">{item.label}:</span>
            <span className="duration-list-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
  // Custom card for listing cancelled sessions by type (merged)
  const CancelledSessionCard = ({ data }) => (
    <div className="duration-list-card">
      <div className="duration-list-title">Cancelled Session:</div>
      <div className="duration-list-content">
        {data.map(item => (
          <div className="duration-list-item" key={item.label}>
            <span className="duration-list-label">{item.label}:</span>
            <span className="duration-list-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Custom card for merged session cancellation rates
  const SessionCancellationRateCard = ({ overall, byStudent, byTeacher }) => (
    <div className="duration-list-card">
      <div className="duration-list-title">Session Cancellation Rate</div>
      <div className="duration-list-content">
        <div className="duration-list-item">
          <span className="duration-list-label">Overall:</span>
          <span className="duration-list-value">{overall}%</span>
        </div>
        <div className="duration-list-item">
          <span className="duration-list-label">By Learner:</span>
          <span className="duration-list-value">{byStudent}%</span>
        </div>
        <div className="duration-list-item">
          <span className="duration-list-label">By Instructor:</span>
          <span className="duration-list-value">{byTeacher}%</span>
        </div>
      </div>
    </div>
  );

  // Skeleton loader for subject demand table
  const SubjectDemandTableSkeleton = () => (
    <div className="subject-demand-table-container">
      <table className="subject-demand-table">
        <thead>
          <tr>
            <th className="subject-demand-table-header">Subject</th>
            <th className="subject-demand-table-header">Bookings</th>
            <th className="subject-demand-table-header">Revenue</th>
            <th className="subject-demand-table-header">Growth</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, index) => (
            <tr key={index} className="subject-demand-table-row">
              <td className="subject-demand-table-cell subject-demand-table-cell-subject">
                <div className="skeleton-text" style={{ height: '12px', width: '100%' }} />
              </td>
              <td className="subject-demand-table-cell subject-demand-table-cell-count">
                <div
                  className="skeleton-text"
                  style={{ height: '12px', width: '60%', margin: '0 auto' }}
                />
              </td>
              <td className="subject-demand-table-cell subject-demand-table-cell-revenue">
                <div
                  className="skeleton-text"
                  style={{ height: '12px', width: '70%', marginLeft: 'auto' }}
                />
              </td>
              <td className="subject-demand-table-cell subject-demand-table-cell-growth">
                <div
                  className="skeleton-text"
                  style={{ height: '12px', width: '50%', margin: '0 auto' }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Helper function to render subject demand table
  const renderSubjectDemandTable = subjects => {
    if (!subjects || subjects.length === 0) {
      return <div className="subject-demand-empty-state">No subjects available</div>;
    }

    return (
      <div className="subject-demand-table-container">
        <table className="subject-demand-table">
          <thead>
            <tr>
              <th className="subject-demand-table-header">Subject</th>
              <th className="subject-demand-table-header">Bookings</th>
              <th className="subject-demand-table-header">Revenue</th>
              <th className="subject-demand-table-header">Growth</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((subject, index) => (
              <tr key={subject.id || index} className="subject-demand-table-row">
                <td className="subject-demand-table-cell subject-demand-table-cell-subject">
                  {subject.title}
                </td>
                <td className="subject-demand-table-cell subject-demand-table-cell-count">
                  {subject.booking_count}
                </td>
                {/* Riyals learners paid (the API converts Koins x 7.5) */}
                <td className="subject-demand-table-cell subject-demand-table-cell-revenue">
                  QR {(subject.total_revenue ?? 0).toFixed(2)}
                </td>
                {/* growth_rate null = no bookings last period: a new subject */}
                <td className="subject-demand-table-cell subject-demand-table-cell-growth">
                  {subject.growth_rate === null || subject.growth_rate === undefined
                    ? 'New'
                    : `${subject.growth_rate.toFixed(1)}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper function to map time period to subject demand API format
  const mapTimePeriodForSubjectDemand = period => {
    switch (period) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  };

  // Helper function to map time period for new APIs
  const mapTimePeriodForNewAPIs = period => {
    switch (period) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  };

  return (
    <Box className="session-analytics-section">
      <Typography className="session-analytics-title">Session Analytics</Typography>
      <Grid container spacing={2} className="session-analytics-cards">
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Total Sessions"
              value={totalSessions}
              growth={totalSessionsGrowth}
              unavailable={!sessionMetricsData}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Booking Ratio"
              value={`${bookingRatio}%`}
              growth={bookingRatioGrowth}
              unavailable={!sessionMetricsData}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Avg. Booking Time"
              value={avgBookingTimeDisplay}
              growth={avgBookingTimeGrowth}
              unavailable={!sessionMetricsData}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Completion Rate"
              value={`${completionRate}%`}
              growth={completionRateGrowth}
              unavailable={!sessionMetricsData}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <CardSkeleton />
          ) : (
            <AnalyticsCard
              title="Repeat Session Rate"
              value={`${repeatSessionRate}%`}
              growth={repeatSessionRateGrowth}
              unavailable={!repeatSessionData}
              comparisonPeriod={getComparisonPeriodText()}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <CardSkeleton />
          ) : !cancellationMetricsData ? (
            <SectionUnavailable title="Session Cancellation Rate" />
          ) : (
            <SessionCancellationRateCard
              overall={sessionCancellation.overall}
              byStudent={sessionCancellation.byStudent}
              byTeacher={sessionCancellation.byTeacher}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <CardSkeleton />
          ) : !cancellationMetricsData ? (
            <SectionUnavailable title="Cancelled Session" />
          ) : (
            <CancelledSessionCard data={sessionCancellation.byType} />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <CardSkeleton />
          ) : !cancellationMetricsData ? (
            <SectionUnavailable title="Major Cancellation Reasons" />
          ) : (
            <CancellationReasonsCard data={topCancellationReasons} />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} className="subject-session-container">
        <Grid item xs={12} md={8}>
          <Box className="subject-demand-container">
            <Typography className="section-title">Subject Demand</Typography>
            <Box className="subject-grid">
              <Box className="subject-card">
                <Typography className="subject-title">Highest Subject Demand</Typography>
                {loading ? (
                  <SubjectDemandTableSkeleton />
                ) : !subjectDemandData ? (
                  <Typography className="card-value">--</Typography>
                ) : (
                  renderSubjectDemandTable(subjectDemandData?.highest_demand?.subjects)
                )}
              </Box>
              <Box className="subject-card">
                <Typography className="subject-title">Lowest Subject Demand</Typography>
                {loading ? (
                  <SubjectDemandTableSkeleton />
                ) : !subjectDemandData ? (
                  <Typography className="card-value">--</Typography>
                ) : (
                  renderSubjectDemandTable(subjectDemandData?.lowest_demand?.subjects)
                )}
              </Box>
              <Box className="subject-card">
                <Typography className="subject-title">Fastest Growing Subject</Typography>
                {loading ? (
                  <SubjectDemandTableSkeleton />
                ) : !subjectDemandData ? (
                  <Typography className="card-value">--</Typography>
                ) : (
                  renderSubjectDemandTable(subjectDemandData?.fastest_growing?.subjects)
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          {loading ? (
            <DonutSkeleton />
          ) : !sessionMetricsData ? (
            <SectionUnavailable title="Session Type" />
          ) : (
            <DonutChart
              title="Session Type"
              centerValue={donutTotal}
              data={sessionTypeData}
              colors={['#2563EB', '#7BCB8F', '#FFCA28']}
              icons={[OneOnOneSessionIcon, GroupSessionIcon, PrimeSessionIcon]}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} className="analytics-charts">
        <Grid item xs={12}>
          <SessionLineChart title="Session" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SessionAnalytics;
